
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/contexts/UserContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";

// Define the exact allowed leave types that match the database constraint
const DB_LEAVE_TYPES = ["REPO", "LEAVE", "OTHER"] as const;
type LeaveType = typeof DB_LEAVE_TYPES[number];

// Schema for the leave request form
const formSchema = z.object({
  leave_type: z.enum(DB_LEAVE_TYPES, {
    required_error: "Please select a leave type",
  }),
  startDate: z.date({
    required_error: "Please select a start date",
  }),
  endDate: z.date().optional(),
  reason: z.string().optional(),
});

interface LeaveRequestFormProps {
  onSuccess?: () => void;
}

const LeaveRequestForm: React.FC<LeaveRequestFormProps> = ({ onSuccess }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      leave_type: undefined,
      reason: "",
    },
  });

  const selectedType = form.watch("leave_type");
  const isSingleDateType = selectedType === "REPO" || selectedType === "OTHER";
  const { isSubmitting } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    console.log("Form submit started", { user: user?.id, values });
    
    if (!user) {
      console.error("No user found");
      toast({
        title: t("authenticationRequired"),
        description: t("mustBeLoggedIn"),
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("Fetching team for user:", user.id);
      // Get user's team
      const { data: teamMembers, error: teamError } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('profile_id', user.id);

      console.log("Team fetch result:", { teamMembers, teamError });

      if (teamError) {
        console.error("Team fetch error:", teamError);
        throw new Error("Failed to fetch team information");
      }

      if (!teamMembers || teamMembers.length === 0) {
        console.error("No team found for user");
        throw new Error("No team found for user");
      }

      const teamMember = teamMembers[0];
      console.log("Using team:", teamMember.team_id);

      // For single date types or when no end date is provided, use start date as end date
      const endDate = isSingleDateType || !values.endDate 
        ? values.startDate
        : values.endDate;
      
      console.log("Inserting leave request...");
      const { error } = await supabase.from("leave_requests").insert({
        user_id: user.id,
        team_id: teamMember.team_id,
        leave_type: values.leave_type,
        start_date: format(values.startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        reason: values.reason,
      });

      if (error) {
        console.error("Insert error:", error);
        throw error;
      }

      console.log("Leave request inserted successfully");

      // Invalidate queries to trigger real-time update
      queryClient.invalidateQueries({ queryKey: ['leave-requests', user.id] });
      queryClient.invalidateQueries({ queryKey: ['leave-stats'] });

      toast({
        title: t("success"),
        description: t("leaveRequestSubmitted"),
      });
      
      form.reset();
      if (onSuccess) onSuccess();
      console.log("Form submission complete");
    } catch (error: any) {
      console.error("Leave request error:", error);
      toast({
        title: t("error"),
        description: error.message || t("failedToSubmit"),
        variant: "destructive",
      });
    }
  };

  // Define the available leave types based on locale - using the exact values that match the database constraint
  const leaveTypes = [
    { value: "REPO", label: t("repo") },
    { value: "LEAVE", label: t("leave") },
    { value: "OTHER", label: t("other") },
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="leave_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("leaveType")}</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isSubmitting}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectLeaveType")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>{t("startDate")}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                      disabled={isSubmitting}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>{t("pickDate")}</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[560px] max-w-[90vw] p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) =>
                      date < new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isSingleDateType && (
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("endDate")}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isSubmitting}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>{t("pickDate")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-[560px] max-w-[90vw] p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        const startDate = form.getValues("startDate");
                        return (
                          date <
                          new Date(
                            startDate
                              ? startDate
                              : new Date().setHours(0, 0, 0, 0)
                          )
                        );
                      }}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("reasonOptional")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("leaveRequestInfo")}
                  className="resize-none"
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isSubmitting}>
          {t("submitNewRequest")}
        </Button>
      </form>
    </Form>
  );
};

export default LeaveRequestForm;
